/**
 * PalladiumFlipbook — PDF Flipbook Initializer (Optimizado con Lazy Loading)
 *
 * Estrategia: el flipbook se crea UNA vez y se mantiene vivo dentro del
 * overlay oculto. Al cerrar solo se oculta el overlay; al reabrir se llama
 * a update() para refrescar el render. Así evitamos el ciclo
 * destroy → re-init que corrompe el contenedor de StPageFlip.
 */
(function () {
    'use strict';

    // Self-hosted (vendored pdf.js 4.9.155 + page-flip 2.0.7). The pdf.js ES modules
    // are served as .js so the static-file MIME is text/javascript (module-import safe).
    // No cross-origin request for ad blockers to strip.
    var PDFJS_CDN = '/_content/Palladium.PageBuilder/js/lib/pdfjs/pdf.min.js';
    var PDFJS_WORKER_CDN = '/_content/Palladium.PageBuilder/js/lib/pdfjs/pdf.worker.min.js';
    var PAGEFLIP_CDN = '/_content/Palladium.PageBuilder/js/lib/page-flip/page-flip.browser.js';

    // { flipbook, pdf, pdfUrl, totalPages, overlayId, renderPage }
    var instances = {};
    var _loadCache = {};
    var _originalStyles = {};

    // ── Overlay activo (solo uno a la vez) ──
    var _activeContainerId = null;

    // ── Zoom ──
    var ZOOM_MIN = 0.5;
    var ZOOM_MAX = 3.0;
    var ZOOM_STEP = 0.25;

    // Shared loader (pd-load.js, loaded before this file).
    function loadScript(url) { return PdLoad.script(url); }

    function loadModule(url) {
        if (_loadCache[url]) return _loadCache[url];
        _loadCache[url] = import(url);
        return _loadCache[url];
    }

    function resetContainer(containerId) {
        var el = document.getElementById(containerId);
        if (!el) return;
        el.innerHTML = '';
        el.className = '';
        if (_originalStyles[containerId]) {
            el.style.cssText = _originalStyles[containerId];
        }
    }

    function fullCleanup(containerId) {
        var inst = instances[containerId];
        if (!inst) return;
        try { inst.flipbook.destroy(); } catch (e) {}
        delete instances[containerId];
        resetContainer(containerId);
    }

    // ── Cierre del overlay activo ──
    function closeActive() {
        if (!_activeContainerId) return;
        var inst = instances[_activeContainerId];
        if (inst) {
            // Reset zoom + pan on close
            inst.zoomLevel = 1;
            inst.translateX = 0;
            inst.translateY = 0;
            applyZoom(_activeContainerId);

            if (inst.overlayId) {
                var overlay = document.getElementById(inst.overlayId);
                if (overlay) overlay.style.display = 'none';
            }
        }
        _activeContainerId = null;
    }

    // ── Teclado global: ← → Esc ──
    document.addEventListener('keydown', function (e) {
        if (!_activeContainerId) return;
        var inst = instances[_activeContainerId];
        if (!inst) return;

        // No capturar teclas si el foco está en un input (el go-to-page)
        if (document.activeElement && document.activeElement.tagName === 'INPUT') {
            if (e.key === 'Escape') {
                document.activeElement.blur();
                e.preventDefault();
            }
            return;
        }

        if (e.key === 'ArrowLeft') {
            inst.flipbook.flipPrev();
            e.preventDefault();
        } else if (e.key === 'ArrowRight') {
            inst.flipbook.flipNext();
            e.preventDefault();
        } else if (e.key === 'Escape') {
            closeActive();
            e.preventDefault();
        } else if (e.key === '+' || e.key === '=') {
            zoomIn(_activeContainerId);
            e.preventDefault();
        } else if (e.key === '-') {
            zoomOut(_activeContainerId);
            e.preventDefault();
        } else if (e.key === '0') {
            resetZoom(_activeContainerId);
            e.preventDefault();
        }
    });

    // ── Actualizar paginación (texto + input) ──
    function updatePageDisplay(containerId, currentPageNum, totalPages) {
        var spanEl = document.getElementById(containerId + '-page');
        if (spanEl) spanEl.textContent = '/ ' + totalPages;
        var inputEl = document.getElementById(containerId + '-goto');
        if (inputEl) {
            inputEl.value = currentPageNum;
            inputEl.max = totalPages;
        }
    }

    // ── Link layer: PDF link annotations are NOT drawn on the canvas, so we
    //    overlay <a> elements positioned via the viewport transform. ──

    async function resolveDestPage(pdf, dest) {
        try {
            var explicit = typeof dest === 'string' ? await pdf.getDestination(dest) : dest;
            if (!explicit || !explicit.length) return null;
            var idx = await pdf.getPageIndex(explicit[0]);
            return idx + 1;
        } catch (e) { return null; }
    }

    async function buildLinkLayer(containerId, pdf, page, cssViewport, offsetX, offsetY, parentEl) {
        var annotations;
        try { annotations = await page.getAnnotations({ intent: 'display' }); }
        catch (e) { return; }

        var old = parentEl.querySelector(':scope > .pb-flipbook-link-layer');
        if (old) old.remove();
        if (!annotations || !annotations.length) return;

        var layer = document.createElement('div');
        layer.className = 'pb-flipbook-link-layer';
        layer.style.cssText = 'position:absolute; left:' + offsetX + 'px; top:' + offsetY +
            'px; width:' + cssViewport.width + 'px; height:' + cssViewport.height +
            'px; z-index:5; pointer-events:none;';

        annotations.forEach(function (a) {
            if (a.subtype !== 'Link') return;
            var url = a.url || a.unsafeUrl;
            if (!url && !a.dest) return;

            var r = cssViewport.convertToViewportRectangle(a.rect);
            var left = Math.min(r[0], r[2]);
            var top = Math.min(r[1], r[3]);
            var w = Math.abs(r[2] - r[0]);
            var h = Math.abs(r[3] - r[1]);

            var link = document.createElement('a');
            link.style.cssText = 'position:absolute; left:' + left + 'px; top:' + top +
                'px; width:' + w + 'px; height:' + h + 'px; cursor:pointer; pointer-events:auto;';

            if (url) {
                link.href = url;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
            } else {
                link.href = 'javascript:void(0)';
                (function (dest) {
                    link.addEventListener('click', function (ev) {
                        ev.preventDefault();
                        ev.stopPropagation();
                        resolveDestPage(pdf, dest).then(function (pageNum) {
                            if (pageNum) goToPage(containerId, pageNum);
                        });
                    });
                })(a.dest);
            }

            // Stop StPageFlip / swipe handlers from hijacking the click as a flip
            ['mousedown', 'pointerdown', 'touchstart'].forEach(function (evt) {
                link.addEventListener(evt, function (e) { e.stopPropagation(); }, { passive: true });
            });

            layer.appendChild(link);
        });

        parentEl.appendChild(layer);
    }

    // ── Image-mode helpers (server pre-rendered WebP set) ──

    // Fits a page image inside a box (object-fit: contain) and returns the
    // displayed rectangle, so the link layer can be positioned to match.
    function containRect(pageW, pageH, boxW, boxH) {
        var ar = (pageW && pageH) ? pageW / pageH : boxW / boxH;
        var dispW, dispH;
        if (ar > boxW / boxH) { dispW = boxW; dispH = boxW / ar; }
        else { dispH = boxH; dispW = boxH * ar; }
        return { w: dispW, h: dispH, x: (boxW - dispW) / 2, y: (boxH - dispH) / 2 };
    }

    function buildImageLinkLayer(containerId, page, rect, parentEl) {
        var old = parentEl.querySelector(':scope > .pb-flipbook-link-layer');
        if (old) old.remove();
        var links = (page && page.links) || [];
        if (!links.length) return;

        var layer = document.createElement('div');
        layer.className = 'pb-flipbook-link-layer';
        layer.style.cssText = 'position:absolute; left:' + rect.x + 'px; top:' + rect.y +
            'px; width:' + rect.w + 'px; height:' + rect.h + 'px; z-index:5; pointer-events:none;';

        links.forEach(function (lk) {
            var a = document.createElement('a');
            if (lk.url) {
                // External link → open in a new tab.
                a.href = lk.url;
                a.target = '_blank';
                a.rel = 'noopener noreferrer';
            } else if (lk.page) {
                // Internal page-jump link (arrows, "back to summary", …) → flip to page.
                a.href = 'javascript:void(0)';
                (function (targetPage) {
                    a.addEventListener('click', function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        goToPage(containerId, targetPage);
                    });
                })(lk.page);
            } else {
                return;
            }
            a.style.cssText = 'position:absolute; left:' + (lk.x * rect.w) + 'px; top:' + (lk.y * rect.h) +
                'px; width:' + (lk.w * rect.w) + 'px; height:' + (lk.h * rect.h) +
                'px; cursor:pointer; pointer-events:auto;';
            ['mousedown', 'pointerdown', 'touchstart'].forEach(function (evt) {
                a.addEventListener(evt, function (e) { e.stopPropagation(); }, { passive: true });
            });
            layer.appendChild(a);
        });

        parentEl.appendChild(layer);
    }

    // Renders page `pageNum` as an <img> into `targetDiv`, with the thumbnail as an
    // instant LQIP background that is cleared once the sharper image loads.
    function renderImageInto(containerId, manifest, targetDiv, pageNum, boxW, boxH) {
        var page = manifest.pages[pageNum - 1];
        if (!page) return;

        targetDiv.style.position = 'relative';
        targetDiv.style.backgroundColor = '#ffffff';
        if (page.thumb) {
            targetDiv.style.backgroundImage = "url('" + page.thumb + "')";
            targetDiv.style.backgroundSize = 'contain';
            targetDiv.style.backgroundPosition = 'center';
            targetDiv.style.backgroundRepeat = 'no-repeat';
        }

        var img = document.createElement('img');
        img.alt = '';
        img.draggable = false;
        img.style.cssText = 'display:block; width:' + boxW + 'px; height:' + boxH + 'px; object-fit:contain; background:#ffffff;';
        img.addEventListener('load', function () { targetDiv.style.backgroundImage = 'none'; });
        img.src = page.med || page.high;

        targetDiv.innerHTML = '';
        targetDiv.appendChild(img);

        var rect = containRect(page.width, page.height, boxW, boxH);
        buildImageLinkLayer(containerId, page, rect, targetDiv);
    }

    async function init(containerId, pdfUrl, width, height, showControls, manifestUrl) {
        try {
            var existing = instances[containerId];

            // ── FAST PATH: misma URL y la instancia sigue viva en el DOM ──
            if (existing && existing.pdfUrl === pdfUrl) {
                var container = document.getElementById(containerId);
                if (container && (container.querySelector('.stf__wrapper') || container.querySelector('.pb-mobile-canvas'))) {
                    await new Promise(function (r) { requestAnimationFrame(r); });
                    try { existing.flipbook.update(); } catch (e) {}

                    var cp = 1;
                    try { cp = existing.flipbook.getCurrentPageIndex() + 1; } catch (e) {}
                    updatePageDisplay(containerId, cp, existing.totalPages);

                    _activeContainerId = containerId;
                    return;
                }
                delete instances[containerId];
            } else if (existing) {
                fullCleanup(containerId);
            }

            // ── FULL INIT ──
            var isMobile = window.innerWidth <= 768;

            // Prefer the server-side image set (manifest) when present; otherwise
            // fall back to rasterizing the PDF in the client with pdf.js.
            var manifest = null, useImages = false;
            if (manifestUrl) {
                try {
                    var mResp = await fetch(manifestUrl);
                    if (mResp.ok) {
                        manifest = await mResp.json();
                        useImages = !!(manifest && manifest.numPages > 0 && manifest.pages && manifest.pages.length);
                    }
                } catch (e) { useImages = false; }
            }

            var pdfjsLib = null, pdf = null, totalPages = 0;
            if (useImages) {
                totalPages = manifest.numPages;
                // Desktop still needs StPageFlip for the page-flip effect.
                if (!isMobile) await loadScript(PAGEFLIP_CDN);
            } else {
                var loadPromises = [loadModule(PDFJS_CDN)];
                if (!isMobile) loadPromises.push(loadScript(PAGEFLIP_CDN));
                var [pdfjsModule] = await Promise.all(loadPromises);
                pdfjsLib = pdfjsModule;
                pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER_CDN;
                pdf = await pdfjsLib.getDocument(pdfUrl).promise;
                totalPages = pdf.numPages;
            }

            var container = document.getElementById(containerId);
            if (!container) return;

            // Detectar el overlayId desde el DOM
            var overlayEl = container.closest('.pb-flipbook-overlay');
            var overlayId = overlayEl ? overlayEl.id : null;

            if (!_originalStyles[containerId]) {
                _originalStyles[containerId] = container.style.cssText;
            } else {
                container.style.cssText = _originalStyles[containerId];
                container.className = '';
            }
            container.innerHTML = '';

            await new Promise(function (r) { requestAnimationFrame(r); });

            var rect = container.getBoundingClientRect();
            var actualWidth = rect.width > 0 ? rect.width : width;
            var actualHeight = rect.height > 0 ? rect.height : height;

            var flipbook, renderPage;

            if (isMobile) {
                // ── MOBILE: Single-page viewer (bypasses StPageFlip) ──
                var mobileCanvas, mobileCtx, mobileImg;
                if (useImages) {
                    mobileImg = document.createElement('img');
                    mobileImg.className = 'pb-mobile-img';
                    mobileImg.alt = '';
                    mobileImg.draggable = false;
                    mobileImg.style.cssText = 'display:block; max-width:100%; max-height:100%; margin:auto; background:#fff; border-radius:4px;';
                    container.appendChild(mobileImg);
                } else {
                    mobileCanvas = document.createElement('canvas');
                    mobileCanvas.className = 'pb-mobile-canvas';
                    mobileCanvas.style.cssText = 'display:block; background:#fff; border-radius:4px; margin:auto;';
                    container.appendChild(mobileCanvas);
                    mobileCtx = mobileCanvas.getContext('2d');
                }
                var mobileEl = useImages ? mobileImg : mobileCanvas;

                var currentPage = 1;
                var mobileRendering = false;
                var eventHandlers = { flip: [], changeState: [] };

                async function mobileRenderPage(pageNum) {
                    if (pageNum < 1 || pageNum > totalPages) return;
                    mobileRendering = true;
                    try {
                        if (useImages) {
                            var ip = manifest.pages[pageNum - 1];
                            if (ip) {
                                mobileImg.src = ip.high || ip.med;
                                // Overlay the clickable link layer over the centered image
                                // (same normalized rects the desktop path uses).
                                container.style.position = 'relative';
                                var mcRect = container.getBoundingClientRect();
                                var mcw = mcRect.width > 0 ? mcRect.width : actualWidth;
                                var mch = mcRect.height > 0 ? mcRect.height : actualHeight;
                                var mRect = containRect(ip.width, ip.height, mcw, mch);
                                buildImageLinkLayer(containerId, ip, mRect, container);
                            }
                        } else {
                            var page = await pdf.getPage(pageNum);
                            var vp = page.getViewport({ scale: 1 });
                            var dpr = window.devicePixelRatio || 1;
                            // Re-read container size for orientation changes
                            var cRect = container.getBoundingClientRect();
                            var cw = cRect.width > 0 ? cRect.width : actualWidth;
                            var ch = cRect.height > 0 ? cRect.height : actualHeight;
                            var scale = Math.min(cw / vp.width, ch / vp.height);
                            var sv = page.getViewport({ scale: scale * dpr });

                            mobileCanvas.width = sv.width;
                            mobileCanvas.height = sv.height;
                            mobileCanvas.style.width = Math.floor(sv.width / dpr) + 'px';
                            mobileCanvas.style.height = Math.floor(sv.height / dpr) + 'px';

                            mobileCtx.clearRect(0, 0, sv.width, sv.height);
                            await page.render({ canvasContext: mobileCtx, viewport: sv }).promise;

                            container.style.position = 'relative';
                            var cssVp = page.getViewport({ scale: scale });
                            await buildLinkLayer(containerId, pdf, page, cssVp,
                                mobileCanvas.offsetLeft, mobileCanvas.offsetTop, container);
                        }

                        eventHandlers.flip.forEach(function (fn) {
                            fn({ data: pageNum - 1 });
                        });
                    } finally {
                        mobileRendering = false;
                    }
                }

                // Compatibility API (same shape as StPageFlip)
                flipbook = {
                    flipPrev: function () {
                        if (currentPage > 1 && !mobileRendering) {
                            currentPage--;
                            mobileRenderPage(currentPage);
                            updatePageDisplay(containerId, currentPage, totalPages);
                        }
                    },
                    flipNext: function () {
                        if (currentPage < totalPages && !mobileRendering) {
                            currentPage++;
                            mobileRenderPage(currentPage);
                            updatePageDisplay(containerId, currentPage, totalPages);
                        }
                    },
                    getCurrentPageIndex: function () { return currentPage - 1; },
                    turnToPage: function (idx) {
                        var p = idx + 1;
                        if (p >= 1 && p <= totalPages && !mobileRendering) {
                            currentPage = p;
                            mobileRenderPage(currentPage);
                            updatePageDisplay(containerId, currentPage, totalPages);
                        }
                    },
                    update: function () { mobileRenderPage(currentPage); },
                    destroy: function () { container.innerHTML = ''; },
                    on: function (event, handler) {
                        if (eventHandlers[event]) eventHandlers[event].push(handler);
                    }
                };

                // Swipe navigation (only when not zoomed)
                var swipeStartX = 0, swiping = false;
                mobileEl.addEventListener('touchstart', function (e) {
                    var inst = instances[containerId];
                    if (e.touches.length === 1 && (!inst || inst.zoomLevel <= 1.05)) {
                        swiping = true;
                        swipeStartX = e.touches[0].clientX;
                    }
                }, { passive: true });
                mobileEl.addEventListener('touchend', function (e) {
                    if (!swiping || e.changedTouches.length === 0) { swiping = false; return; }
                    var inst = instances[containerId];
                    if (inst && inst.zoomLevel > 1.05) { swiping = false; return; }
                    var dx = e.changedTouches[0].clientX - swipeStartX;
                    swiping = false;
                    if (Math.abs(dx) > 40) {
                        if (dx < 0) flipbook.flipNext();
                        else flipbook.flipPrev();
                    }
                }, { passive: true });

                renderPage = function () {}; // no-op for mobile

                await mobileRenderPage(1);
            } else {
                // ── DESKTOP: StPageFlip double-page spread ──
                var pageWidth = Math.floor(actualWidth / 2);
                var pageHeight = actualHeight;

                var pageElements = [];
                var renderedPages = new Set();

                for (var i = 1; i <= totalPages; i++) {
                    var pageDiv = document.createElement('div');
                    pageDiv.className = 'pb-flipbook-page';
                    pageDiv.innerHTML = '';
                    pageDiv.style.backgroundColor = '#ffffff';
                    container.appendChild(pageDiv);
                    pageElements.push(pageDiv);
                }

                flipbook = new St.PageFlip(container, {
                    width: pageWidth,
                    height: pageHeight,
                    size: 'fixed',
                    minWidth: 200,
                    maxWidth: pageWidth,
                    minHeight: 200,
                    maxHeight: pageHeight,
                    showCover: true,
                    maxShadowOpacity: 0.5,
                    mobileScrollSupport: false,
                    flippingTime: 800,
                    useMouseEvents: true,
                    swipeDistance: 30,
                    autoSize: true,
                    drawShadow: true,
                });

                flipbook.loadFromHTML(container.querySelectorAll('.pb-flipbook-page'));

                renderPage = async function (pageNum) {
                    if (pageNum < 1 || pageNum > totalPages || renderedPages.has(pageNum)) return;
                    renderedPages.add(pageNum);

                    if (useImages) {
                        renderImageInto(containerId, manifest, pageElements[pageNum - 1], pageNum, pageWidth, pageHeight);
                        return;
                    }

                    var page = await pdf.getPage(pageNum);
                    var viewport = page.getViewport({ scale: 1 });
                    var dpr = window.devicePixelRatio || 1;
                    var scale = Math.min(pageWidth / viewport.width, pageHeight / viewport.height);
                    var renderViewport = page.getViewport({ scale: scale * dpr });

                    var canvas = document.createElement('canvas');
                    canvas.width = Math.round(pageWidth * dpr);
                    canvas.height = Math.round(pageHeight * dpr);
                    canvas.style.width = pageWidth + 'px';
                    canvas.style.height = pageHeight + 'px';
                    canvas.style.display = 'block';
                    var ctx = canvas.getContext('2d');

                    ctx.fillStyle = '#ffffff';
                    ctx.fillRect(0, 0, canvas.width, canvas.height);

                    var offsetX = Math.round((canvas.width - renderViewport.width) / 2);
                    var offsetY = Math.round((canvas.height - renderViewport.height) / 2);
                    ctx.translate(offsetX, offsetY);

                    await page.render({ canvasContext: ctx, viewport: renderViewport }).promise;

                    ctx.setTransform(1, 0, 0, 1, 0, 0);

                    var targetDiv = pageElements[pageNum - 1];
                    targetDiv.style.position = 'relative';
                    targetDiv.innerHTML = '';
                    targetDiv.appendChild(canvas);

                    var cssVp = page.getViewport({ scale: scale });
                    await buildLinkLayer(containerId, pdf, page, cssVp,
                        Math.round((pageWidth - cssVp.width) / 2),
                        Math.round((pageHeight - cssVp.height) / 2),
                        targetDiv);
                };

                                flipbook.on('flip', function (e) {
                    var currentPageIndex = e.data;
                    var currentPageNum = currentPageIndex + 1;

                    updatePageDisplay(containerId, currentPageNum, totalPages);

                    for (var p = currentPageNum - 1; p <= currentPageNum + 4; p++) {
                        renderPage(p);
                    }
                });

                flipbook.on('changeState', function (e) {
                    if (e.data === 'user_fold' || e.data === 'flipping') {
                        var idx = flipbook.getCurrentPageIndex();
                        var pageNum = idx + 1;
                        for (var p = pageNum; p <= pageNum + 4; p++) {
                            renderPage(p);
                        }
                    }
                });

                // Pre-renderizar primeras páginas
                var initialPreload = Math.min(totalPages, 5);
                var preloadPromises = [];
                for (var p = 1; p <= initialPreload; p++) {
                    preloadPromises.push(renderPage(p));
                }
                await Promise.all(preloadPromises);
            }

            updatePageDisplay(containerId, 1, totalPages);

            instances[containerId] = {
                flipbook: flipbook,
                pdf: pdf,
                pdfUrl: pdfUrl,
                totalPages: totalPages,
                overlayId: overlayId,
                renderPage: renderPage,
                isMobile: isMobile,
                zoomLevel: 1,
                translateX: 0,
                translateY: 0,
            };

            _activeContainerId = containerId;

            // ── Setup zoom gestures ──
            setupZoomGestures(containerId);

        } catch (err) {
            console.error('[Flipbook] Init error:', err);
            var el = document.getElementById(containerId);
            if (el) el.innerHTML = '<p style="color:#ef4444;text-align:center;">Error cargando el PDF</p>';
        }
    }

    function prev(containerId) {
        var inst = instances[containerId];
        if (inst) inst.flipbook.flipPrev();
    }

    function next(containerId) {
        var inst = instances[containerId];
        if (inst) inst.flipbook.flipNext();
    }

    // Ir a una página concreta (pageNum es 1-based)
    function goToPage(containerId, pageNum) {
        var inst = instances[containerId];
        if (!inst) return;

        pageNum = parseInt(pageNum, 10);
        if (isNaN(pageNum) || pageNum < 1) pageNum = 1;
        if (pageNum > inst.totalPages) pageNum = inst.totalPages;

        var pageIndex = pageNum - 1; // StPageFlip usa 0-based

        // Pre-renderizar páginas alrededor del destino
        for (var p = pageNum - 1; p <= pageNum + 4; p++) {
            inst.renderPage(p);
        }

        try { inst.flipbook.turnToPage(pageIndex); } catch (e) {}
        updatePageDisplay(containerId, pageNum, inst.totalPages);
    }

    function destroy(containerId) {
        // NO destruimos — el flipbook se queda vivo en el overlay oculto.
        _activeContainerId = null;
    }

    // ── Zoom (translate + scale, anchored at cursor / finger position) ──

    function applyZoom(containerId) {
        var inst = instances[containerId];
        var container = document.getElementById(containerId);
        if (!container || !inst) return;

        var zoom = inst.zoomLevel;
        var tx = inst.translateX || 0;
        var ty = inst.translateY || 0;

        if (zoom === 1 && tx === 0 && ty === 0) {
            container.style.transform = '';
        } else {
            container.style.transform = 'translate(' + tx + 'px, ' + ty + 'px) scale(' + zoom + ')';
        }
        container.style.transformOrigin = '0 0';
        container.style.cursor = zoom > 1.05 ? 'grab' : '';

        // Disable StPageFlip interactions when zoomed to prevent
        // page flipping/folding while panning
        var stfWrapper = container.querySelector('.stf__wrapper');
        if (stfWrapper) {
            stfWrapper.style.pointerEvents = zoom > 1.05 ? 'none' : '';
        }

        var zoomEl = document.getElementById(containerId + '-zoom');
        if (zoomEl) zoomEl.textContent = Math.round(zoom * 100) + '%';
    }

    /**
     * Zoom so that the screen point (screenX, screenY) stays visually fixed.
     * Uses: newTx = oldTx + localX * (oldZoom - newZoom)
     */
    function zoomAtPoint(containerId, newZoom, screenX, screenY) {
        var inst = instances[containerId];
        var container = document.getElementById(containerId);
        if (!inst || !container) return;

        var oldZoom = inst.zoomLevel;
        var oldTx = inst.translateX || 0;
        var oldTy = inst.translateY || 0;
        var rect = container.getBoundingClientRect();

        var localX = (screenX - rect.left) / oldZoom;
        var localY = (screenY - rect.top) / oldZoom;

        inst.translateX = oldTx + localX * (oldZoom - newZoom);
        inst.translateY = oldTy + localY * (oldZoom - newZoom);
        inst.zoomLevel = newZoom;

        applyZoom(containerId);
    }

    function zoomIn(containerId) {
        var inst = instances[containerId];
        if (!inst) return;
        var container = document.getElementById(containerId);
        if (!container) return;
        var rect = container.getBoundingClientRect();
        var newZoom = Math.min(ZOOM_MAX, +(inst.zoomLevel + ZOOM_STEP).toFixed(2));
        zoomAtPoint(containerId, newZoom, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    function zoomOut(containerId) {
        var inst = instances[containerId];
        if (!inst) return;
        var container = document.getElementById(containerId);
        if (!container) return;
        var rect = container.getBoundingClientRect();
        var newZoom = Math.max(ZOOM_MIN, +(inst.zoomLevel - ZOOM_STEP).toFixed(2));
        zoomAtPoint(containerId, newZoom, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    function resetZoom(containerId) {
        var inst = instances[containerId];
        if (!inst) return;
        inst.zoomLevel = 1;
        inst.translateX = 0;
        inst.translateY = 0;
        applyZoom(containerId);
    }

    // ── Helpers ──

    function getTouchDistance(touches) {
        var dx = touches[0].clientX - touches[1].clientX;
        var dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
    }

    function getTouchMidpoint(touches) {
        return {
            x: (touches[0].clientX + touches[1].clientX) / 2,
            y: (touches[0].clientY + touches[1].clientY) / 2
        };
    }

    // ── Gesture setup (pinch, wheel, pan, double-tap) ──

    function setupZoomGestures(containerId) {
        var container = document.getElementById(containerId);
        if (!container || container._zoomGesturesReady) return;
        container._zoomGesturesReady = true;

        // ---- Pinch-to-zoom state ----
        var pinchDist0 = 0, pinchZoom0 = 1;
        var pinchTx0 = 0, pinchTy0 = 0;
        var pinchLocalX = 0, pinchLocalY = 0;

        // ---- Touch pan state ----
        var touchPanning = false;
        var tpStartX = 0, tpStartY = 0, tpTx0 = 0, tpTy0 = 0;

        container.addEventListener('touchstart', function (e) {
            var inst = instances[containerId];
            if (!inst) return;

            if (e.touches.length === 2) {
                e.preventDefault();
                touchPanning = false;
                pinchDist0 = getTouchDistance(e.touches);
                pinchZoom0 = inst.zoomLevel;
                pinchTx0 = inst.translateX || 0;
                pinchTy0 = inst.translateY || 0;
                var mid = getTouchMidpoint(e.touches);
                var rect = container.getBoundingClientRect();
                pinchLocalX = (mid.x - rect.left) / pinchZoom0;
                pinchLocalY = (mid.y - rect.top) / pinchZoom0;
            } else if (e.touches.length === 1 && inst.zoomLevel > 1.05) {
                e.preventDefault();
                touchPanning = true;
                tpStartX = e.touches[0].clientX;
                tpStartY = e.touches[0].clientY;
                tpTx0 = inst.translateX || 0;
                tpTy0 = inst.translateY || 0;
            }
        }, { passive: false });

        container.addEventListener('touchmove', function (e) {
            var inst = instances[containerId];
            if (!inst) return;

            if (e.touches.length === 2 && pinchDist0 > 0) {
                e.preventDefault();
                var dist = getTouchDistance(e.touches);
                var newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN,
                    +(pinchZoom0 * dist / pinchDist0).toFixed(2)));
                inst.translateX = pinchTx0 + pinchLocalX * (pinchZoom0 - newZoom);
                inst.translateY = pinchTy0 + pinchLocalY * (pinchZoom0 - newZoom);
                inst.zoomLevel = newZoom;
                applyZoom(containerId);
            } else if (e.touches.length === 1 && touchPanning) {
                e.preventDefault();
                inst.translateX = tpTx0 + (e.touches[0].clientX - tpStartX);
                inst.translateY = tpTy0 + (e.touches[0].clientY - tpStartY);
                applyZoom(containerId);
            }
        }, { passive: false });

        container.addEventListener('touchend', function () {
            if (arguments[0] && arguments[0].touches.length < 2) pinchDist0 = 0;
            if (arguments[0] && arguments[0].touches.length === 0) touchPanning = false;
        });

        // ---- Wheel zoom at cursor (desktop) ----
        container.addEventListener('wheel', function (e) {
            e.preventDefault();
            var inst = instances[containerId];
            if (!inst) return;
            var delta = e.deltaY > 0 ? -ZOOM_STEP : ZOOM_STEP;
            var newZoom = Math.min(ZOOM_MAX, Math.max(ZOOM_MIN,
                +(inst.zoomLevel + delta).toFixed(2)));
            zoomAtPoint(containerId, newZoom, e.clientX, e.clientY);
        }, { passive: false });

        // ---- Mouse drag-to-pan when zoomed (desktop) ----
        var mousePanning = false;
        var mpStartX = 0, mpStartY = 0, mpTx0 = 0, mpTy0 = 0;

        container.addEventListener('mousedown', function (e) {
            var inst = instances[containerId];
            if (!inst || inst.zoomLevel <= 1.05) return;
            mousePanning = true;
            mpStartX = e.clientX;
            mpStartY = e.clientY;
            mpTx0 = inst.translateX || 0;
            mpTy0 = inst.translateY || 0;
            container.style.cursor = 'grabbing';
            e.preventDefault();
        });

        document.addEventListener('mousemove', function (e) {
            if (!mousePanning) return;
            var inst = instances[containerId];
            if (!inst) return;
            inst.translateX = mpTx0 + (e.clientX - mpStartX);
            inst.translateY = mpTy0 + (e.clientY - mpStartY);
            applyZoom(containerId);
        });

        document.addEventListener('mouseup', function () {
            if (!mousePanning) return;
            mousePanning = false;
            var inst = instances[containerId];
            var c = document.getElementById(containerId);
            if (c) c.style.cursor = inst && inst.zoomLevel > 1.05 ? 'grab' : '';
        });

        // ---- Double-tap to toggle zoom at tap position (mobile) ----
        var lastTap = 0;
        container.addEventListener('touchend', function (e) {
            if (e.touches.length > 0) return;
            var now = Date.now();
            if (now - lastTap < 300) {
                e.preventDefault();
                var inst = instances[containerId];
                if (inst && e.changedTouches.length > 0) {
                    var t = e.changedTouches[0];
                    if (inst.zoomLevel > 1.1) {
                        resetZoom(containerId);
                    } else {
                        zoomAtPoint(containerId, 2, t.clientX, t.clientY);
                    }
                }
            }
            lastTap = now;
        });
    }

    window.PalladiumFlipbook = {
        init: init,
        prev: prev,
        next: next,
        goToPage: goToPage,
        destroy: destroy,
        closeActive: closeActive,
        zoomIn: zoomIn,
        zoomOut: zoomOut,
        resetZoom: resetZoom,
    };
})();